/**
 * Bigens motion (T46): <bigens-bounce>, the bouncing ball from snippets/bigens-bounce.liquid.
 *
 * - Plays only while on screen (IntersectionObserver); scrolling it away pauses it mid-bounce.
 * - One run is `--bb-loops` bounces (CSS); then it rests on the ground (data-state="rest").
 * - Coming back into view, or the pointer moving over it (or over the closest [data-bigens-bounce-trigger]),
 *   starts a new run once the last one has finished. A run never lasts more than 5 s (WCAG 2.2.2).
 * - prefers-reduced-motion: reduce → nothing runs; the CSS also removes the animation.
 * No copy in JS; the element is decorative (aria-hidden in Liquid).
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class BigensBounce extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {Element | null} */
  #trigger = null;
  #visible = false;

  connectedCallback() {
    if (reducedMotion.matches) return;
    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.35 });
    this.#observer.observe(this);
    this.#trigger = this.closest('[data-bigens-bounce-trigger]') || this;
    this.#trigger.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('animationend', this.#onAnimationEnd);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#trigger?.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('animationend', this.#onAnimationEnd);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;
    this.#visible = entry.isIntersecting;
    if (this.#visible) {
      if (this.dataset.state === 'play') {
        delete this.dataset.paused;
      } else {
        this.#play();
      }
    } else if (this.dataset.state === 'play') {
      this.dataset.paused = '';
    }
  };

  #onPointerEnter = () => {
    if (this.#visible && this.dataset.state !== 'play') this.#play();
  };

  /** @param {AnimationEvent} event */
  #onAnimationEnd = (event) => {
    // The ball's rise animation is the one that sets the pace; ignore the shape and shadow tracks.
    if (event.animationName !== 'bigens-bounce-rise') return;
    this.dataset.state = 'rest';
  };

  #play() {
    if (reducedMotion.matches) return;
    delete this.dataset.paused;
    this.dataset.state = 'rest';
    // Restart from the first frame: drop the animation, flush styles, then set it again.
    void this.offsetWidth;
    this.dataset.state = 'play';
  }
}

if (!customElements.get('bigens-bounce')) {
  customElements.define('bigens-bounce', BigensBounce);
}
